import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LogOut, Trash2, Phone, Mail, Calendar, ExternalLink, Share2 } from "lucide-react";

const STATUSES = ["new", "contacted", "won", "lost", "cancelled"];
const statusColor = {
  new: "text-gold border-gold/40",
  contacted: "text-blue-300 border-blue-400/40",
  won: "text-emerald-300 border-emerald-400/40",
  lost: "text-white/40 border-white/20",
  cancelled: "text-red-300 border-red-400/40",
};

const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
};

export default function Dashboard() {
  const { user, logout, authAxios } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await authAxios().get("/leads");
      setLeads(data);
    } catch {
      toast.error("Could not load leads.");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try { await authAxios().patch(`/leads/${id}`, { status }); }
    catch { toast.error("Update failed."); load(); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await authAxios().delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    } catch { toast.error("Delete failed."); }
  };

  const doLogout = () => { logout(); nav("/login"); };

  const shareLink = async () => {
    const url = `${window.location.origin}/contact`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Book a consultation — Marcus Montgomery Consulting", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Booking link copied to clipboard");
      }
    } catch {
      /* user dismissed share sheet */
    }
  };

  const stats = useMemo(() => ({
    total: leads.length,
    new: leads.filter((l) => l.status === "new").length,
    won: leads.filter((l) => l.status === "won").length,
  }), [leads]);

  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);

  return (
    <div className="min-h-screen bg-[#050507] text-white font-sans" data-testid="dashboard-page">
      <div className="noise" />
      <header className="border-b border-white/10 bg-[#0F0F13]/80 backdrop-blur-2xl sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-serif text-2xl">MM</span>
            <span className="h-4 w-px bg-white/20" />
            <span className="overline text-white/60">Leads Dashboard</span>
          </div>
          <div className="flex items-center gap-3 sm:gap-5">
            <button data-testid="share-btn" onClick={shareLink} className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 overline hover:border-gold hover:text-gold transition-colors">
              <Share2 size={14} /> Share
            </button>
            <Link to="/" className="overline text-white/50 hover:text-gold transition-colors hidden sm:flex items-center gap-1">
              View site <ExternalLink size={12} />
            </Link>
            <span className="text-white/40 text-sm hidden md:block">{user?.email}</span>
            <button data-testid="logout-btn" onClick={doLogout} className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 overline hover:border-gold hover:text-gold transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10 mb-10">
          {[["Total leads", stats.total], ["New / unactioned", stats.new], ["Contracts won", stats.won]].map(([label, val]) => (
            <div key={label} className="bg-[#0F0F13] p-6 md:p-8">
              <div className="font-serif text-4xl md:text-5xl text-gold">{val}</div>
              <div className="overline text-white/40 mt-2">{label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {["all", ...STATUSES].map((s) => (
            <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)}
              className={`overline px-4 py-2 border transition-colors ${filter === s ? "bg-gold text-black border-gold" : "border-white/15 text-white/60 hover:border-white/40"}`}>
              {s}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-white/40 py-20 text-center">Loading leads…</div>
        ) : filtered.length === 0 ? (
          <div data-testid="no-leads" className="text-white/40 py-20 text-center border border-white/10">No leads yet.</div>
        ) : (
          <div className="space-y-px bg-white/10 border border-white/10" data-testid="leads-list">
            {filtered.map((l) => (
              <div key={l.id} data-testid={`lead-${l.id}`} className="bg-[#0F0F13] p-6 md:p-8 grid md:grid-cols-12 gap-6 items-start">
                <div className="md:col-span-4">
                  <div className="font-serif text-2xl tracking-tight">{l.name}</div>
                  {l.company && <div className="text-white/50 text-sm mt-1">{l.company}</div>}
                  <div className="flex flex-col gap-1 mt-3 text-sm">
                    <a href={`mailto:${l.email}`} className="text-white/60 hover:text-gold flex items-center gap-2"><Mail size={13} /> {l.email}</a>
                    {l.phone && <a href={`tel:${l.phone}`} className="text-white/60 hover:text-gold flex items-center gap-2"><Phone size={13} /> {l.phone}</a>}
                  </div>
                </div>
                <div className="md:col-span-4">
                  {l.slot_date && (
                    <div className="flex items-center gap-2 text-gold text-sm mb-2">
                      <Calendar size={14} /> {l.slot_date} · {fmtTime(l.slot_time)} MT
                    </div>
                  )}
                  {l.service_interest && <div className="overline text-white/40 mb-2">{l.service_interest}</div>}
                  {l.message && <p className="text-white/60 text-sm leading-relaxed">{l.message}</p>}
                </div>
                <div className="md:col-span-4 flex items-center justify-between md:justify-end gap-3">
                  <select
                    data-testid={`status-${l.id}`}
                    value={l.status}
                    onChange={(e) => setStatus(l.id, e.target.value)}
                    className={`bg-white/5 border ${statusColor[l.status] || "border-white/20 text-white"} px-3 py-2 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-gold appearance-none capitalize`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s} className="bg-[#0F0F13] text-white">{s}</option>)}
                  </select>
                  <button data-testid={`delete-${l.id}`} onClick={() => remove(l.id)} className="text-white/40 hover:text-red-400 p-2 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
